/*
 * Copyright (C) 2021 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React from 'react'
import {Flex} from '@instructure/ui-flex'
import {IconArrowUpLine, IconArrowDownLine, IconOutcomesLine} from '@instructure/ui-icons'
import {Menu} from '@instructure/ui-menu'
import useModal from '@canvas/outcomes/react/hooks/useModal'
import {useScope as createI18nScope} from '@canvas/i18n'
import {SortBy} from '@canvas/outcomes/react/utils/constants'
import {SortOrder} from '@instructure/outcomes-ui/lib/util/gradebook/constants'
import {Outcome, Student} from '@canvas/outcomes/react/types/rollup'
import {Sorting} from '@canvas/outcomes/react/types/shapes'
import {OutcomeDescriptionModal} from '../modals/OutcomeDescriptionModal'
import {OutcomeDistributionPopover} from '../popovers/OutcomeDistributionPopover'
import {ContributingScoresForOutcome} from '@canvas/outcomes/react/hooks/useContributingScores'
import {ColumnHeader} from '@instructure/outcomes-ui/es/components/Gradebook/gradebook-table/ColumnHeader'
import {OutcomeDistribution} from '@canvas/outcomes/react/types/mastery_distribution'
import {showFlashAlert} from '@instructure/platform-alerts'

const I18n = createI18nScope('learning_mastery_gradebook')

export interface OutcomeHeaderProps {
  outcome: Outcome
  sorting: Sorting
  contributingScoresForOutcome: ContributingScoresForOutcome
  outcomeDistribution?: OutcomeDistribution
  distributionStudents?: Student[]
  courseId: string
}

export const OutcomeHeader: React.FC<OutcomeHeaderProps> = ({
  outcome,
  sorting,
  contributingScoresForOutcome,
  outcomeDistribution,
  distributionStudents,
  courseId,
}) => {
  // OD => OutcomeDescription
  const [isODModalOpen, openODModal, closeODModal] = useModal() as [boolean, () => void, () => void]
  // ODP => OutcomeDistributionPopover
  const [isODPOpen, openODP, closeODP] = useModal() as [boolean, () => void, () => void]

  const isCurrentlySelected =
    sorting.sortBy === SortBy.Outcome && sorting.sortOutcomeId === String(outcome.id)

  const handleSortAscending = () => {
    sorting.setSortBy(SortBy.Outcome)
    sorting.setSortOutcomeId(String(outcome.id))
    sorting.setSortOrder(SortOrder.ASC)
  }

  const handleSortDescending = () => {
    sorting.setSortBy(SortBy.Outcome)
    sorting.setSortOutcomeId(String(outcome.id))
    sorting.setSortOrder(SortOrder.DESC)
  }

  const handleToggleContributingScores = () => {
    const wasVisible = contributingScoresForOutcome.isVisible()
    contributingScoresForOutcome.toggleVisibility()

    const message = wasVisible
      ? I18n.t('Contributing Scores for %{outcome} Hidden', {outcome: outcome.title})
      : I18n.t('Showing Contributing Scores for %{outcome}', {outcome: outcome.title})

    showFlashAlert({
      message,
      type: 'info',
      srOnly: true,
      politeness: 'polite',
    })
  }

  const sortMenuGroup = (
    <Menu.Group label={I18n.t('Sort')} key="sort">
      <Menu.Item
        onSelect={handleSortAscending}
        selected={isCurrentlySelected && sorting.sortOrder === SortOrder.ASC}
      >
        <Flex gap="x-small">
          <IconArrowUpLine spacing="small" />
          {I18n.t('Ascending scores')}
        </Flex>
      </Menu.Item>
      <Menu.Item
        onSelect={handleSortDescending}
        selected={isCurrentlySelected && sorting.sortOrder === SortOrder.DESC}
      >
        <Flex gap="x-small">
          <IconArrowDownLine spacing="small" />
          {I18n.t('Descending scores')}
        </Flex>
      </Menu.Item>
    </Menu.Group>
  )

  const displayMenuGroup = (
    <Menu.Group label={I18n.t('Display')} key="display">
      <Menu.Item onClick={handleToggleContributingScores}>
        {contributingScoresForOutcome.isVisible()
          ? I18n.t('Hide Contributing Scores')
          : I18n.t('Show Contributing Scores')}
      </Menu.Item>
      <Menu.Item onClick={openODModal}>{I18n.t('Outcome Info')}</Menu.Item>
      <Menu.Item onClick={openODP}>{I18n.t('Show Outcome Distribution')}</Menu.Item>
    </Menu.Group>
  )

  return (
    <>
      <ColumnHeader
        title={outcome.title}
        icon={<IconOutcomesLine />}
        optionsMenuTriggerLabel={I18n.t('%{outcome} options', {outcome: outcome.title})}
        optionsMenuItems={[sortMenuGroup, <Menu.Separator key="separator" />, displayMenuGroup]}
      />

      <OutcomeDescriptionModal
        outcome={outcome}
        isOpen={isODModalOpen}
        onCloseHandler={closeODModal}
      />

      {isODPOpen && (
        <OutcomeDistributionPopover
          outcome={outcome}
          outcomeDistribution={outcomeDistribution}
          distributionStudents={distributionStudents}
          courseId={courseId}
          isOpen={isODPOpen}
          onCloseHandler={closeODP}
          renderTrigger={<span />}
        />
      )}
    </>
  )
}
